/**
 * MVP player selector for scorecard upload.
 *
 * Displays a searchable dropdown of players who appeared in the game,
 * grouped by team (Away team first, then Home team).
 */
import { useState, useMemo, useRef, useEffect } from 'react';
import { SearchIcon, XIcon, TrophyIcon, ChevronDownIcon } from 'lucide-react';
import type { GamePlayer } from '../../types/boxscore';

interface MvpSelectorProps {
  /** List of players who appeared in the game */
  players: GamePlayer[];
  /** Away team code for grouping */
  awayTeamCode: string;
  /** Home team code for grouping */
  homeTeamCode: string;
  /** Currently selected player MLB ID */
  selectedMlbId: number | null;
  /** Callback when player is selected */
  onSelect: (mlbId: number | null, name: string | null) => void;
  /** Whether the dropdown is loading */
  isLoading?: boolean;
}

export function MvpSelector({
  players,
  awayTeamCode,
  homeTeamCode,
  selectedMlbId,
  onSelect,
  isLoading = false,
}: MvpSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Get the selected player's name for display
  const selectedPlayer = useMemo(() => {
    return players.find((p) => p.mlb_id === selectedMlbId);
  }, [players, selectedMlbId]);

  // Group players by team and filter by search query
  const { awayPlayers, homePlayers } = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    const filtered = query ? players.filter((p) => p.name.toLowerCase().includes(query)) : players;

    return {
      awayPlayers: filtered.filter((p) => p.team_code === awayTeamCode),
      homePlayers: filtered.filter((p) => p.team_code === homeTeamCode),
    };
  }, [players, awayTeamCode, homeTeamCode, searchQuery]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (player: GamePlayer) => {
    onSelect(player.mlb_id, player.name);
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleClear = () => {
    onSelect(null, null);
    setSearchQuery('');
  };

  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded-lg" />
      </div>
    );
  }

  if (players.length === 0) {
    return (
      <div className="text-sm text-gray-500 dark:text-gray-400 italic">
        No player data available for MVP selection
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`
          w-full flex items-center justify-between px-3 py-2 rounded-lg border
          transition-colors text-left
          ${
            isOpen
              ? 'border-amber-500 ring-2 ring-amber-500/20'
              : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
          }
          bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
        `}
      >
        <div className="flex items-center gap-2 min-w-0">
          <TrophyIcon className="w-4 h-4 text-amber-500 flex-shrink-0" />
          {selectedPlayer ? (
            <span className="truncate font-medium">{selectedPlayer.name}</span>
          ) : (
            <span className="text-gray-500 dark:text-gray-400">Select game MVP (optional)</span>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {selectedPlayer && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  handleClear();
                }
              }}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded cursor-pointer"
            >
              <XIcon className="w-4 h-4 text-gray-400" />
            </span>
          )}
          <ChevronDownIcon
            className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </div>
      </button>

      {/* Dropdown panel */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-80 overflow-hidden">
          {/* Search input */}
          <div className="p-2 border-b border-gray-200 dark:border-gray-700">
            <div className="relative">
              <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search players..."
                className="w-full pl-8 pr-3 py-1.5 text-sm rounded-md border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
              />
            </div>
          </div>

          {/* Player list */}
          <div className="overflow-y-auto max-h-60">
            {awayPlayers.length === 0 && homePlayers.length === 0 ? (
              <div className="px-3 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                No players found
              </div>
            ) : (
              <>
                {/* Away team section */}
                {awayPlayers.length > 0 && (
                  <div>
                    <div className="px-3 py-1.5 bg-gray-50 dark:bg-gray-700/50 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      {awayTeamCode}
                    </div>
                    {awayPlayers.map((player) => (
                      <PlayerOption
                        key={player.mlb_id}
                        player={player}
                        isSelected={player.mlb_id === selectedMlbId}
                        onClick={() => handleSelect(player)}
                      />
                    ))}
                  </div>
                )}

                {/* Home team section */}
                {homePlayers.length > 0 && (
                  <div>
                    <div className="px-3 py-1.5 bg-gray-50 dark:bg-gray-700/50 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      {homeTeamCode}
                    </div>
                    {homePlayers.map((player) => (
                      <PlayerOption
                        key={player.mlb_id}
                        player={player}
                        isSelected={player.mlb_id === selectedMlbId}
                        onClick={() => handleSelect(player)}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

interface PlayerOptionProps {
  player: GamePlayer;
  isSelected: boolean;
  onClick: () => void;
}

function PlayerOption({ player, isSelected, onClick }: PlayerOptionProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`
        w-full flex items-center gap-2 px-3 py-2 text-left text-sm
        transition-colors
        ${
          isSelected
            ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300'
            : 'hover:bg-gray-50 dark:hover:bg-gray-700/50 text-gray-900 dark:text-gray-100'
        }
      `}
    >
      <span className="flex-1 truncate">{player.name}</span>
      <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
        {player.player_type === 'pitcher' ? 'P' : 'B'}
      </span>
      {isSelected && <TrophyIcon className="w-4 h-4 text-amber-500 flex-shrink-0" />}
    </button>
  );
}
